// =============================================================================
// LP Express — "Temecula Passport (Mystery Drop)" template content model.
//
// Imported from the Gaffle landing-template export's `temecula-passport`
// template (Independence Day "mystery drink" lead-capture drop: sign up to
// unlock a STATIC secret code shown at the shop — pay in person). Like the
// Lead Magnet (coffee-shop) template, the ENTIRE page copy lives as ONE
// structured blob at `generatedContent.temeculaPassport` (alongside
// `templateType: "temecula-passport"`), edited in place via the shared
// path-based `setText`/`setLinkUrl` ops (paths like
// ["temeculaPassport","hero","headline"]).
//
// Generation does NOT require a Sim workflow: the export's copy is embedded
// verbatim below as the template's default content, so a new run persists it
// synchronously (a SIM_LP_TEMECULA_PASSPORT_URL workflow can later take over
// async generation, mirroring cold-drip/coffee-shop). Per-section copy
// regeneration uses dynamic block keys `temecula-passport-<sectionKey>`.
//
// Lead capture: the page's two coupon panels POST (on PUBLISHED pages only)
// to the public endpoint /api/public/lp-express/:brandSlug/:slug/leads (the
// same lp_express_leads infrastructure as the Lead Magnet template). Unlike
// the Lead Magnet's per-lead "PREFIX-XXXX" coupons, this template reveals ONE
// static, editable secret code (couponPanel.success.code — default
// "MINTGAFFLE") to every lead; a per-lead tracking code is still generated
// and stored server-side for admin tracking but is NEVER displayed. The
// export's Meta Pixel is intentionally OMITTED (per task spec).
//
// Imagery: the export's asset binaries were NOT included in the export file
// (filenames only), so photo surfaces are AI slots (temeculaPassportImages,
// standard single-slot pipeline) with the usual media-picker/upload override.
// =============================================================================

// ── Section catalog (from the export's sections.json, in order) ─────────────

export const LP_TEMECULA_PASSPORT_SECTIONS: { key: string; label: string }[] = [
  { key: "ticker", label: "Scrolling Ticker" },
  { key: "header", label: "Header (Logo)" },
  { key: "hero", label: "Hero + Mystery Card + Coupon Panel" },
  { key: "poster", label: "Poster Image" },
  { key: "howItWorks", label: "How the Drop Works" },
  { key: "ingredients", label: "What's Inside" },
  { key: "gallery", label: "Gallery" },
  { key: "limitedBanner", label: "Limited Banner" },
  { key: "faq", label: "FAQ" },
  { key: "bottomCta", label: "Bottom CTA + Coupon Panel" },
  { key: "pourBar", label: "Visit the Pour Bar" },
  { key: "footer", label: "Footer" },
  { key: "stickyBar", label: "Sticky Bottom Bar" },
];

export const LP_TEMECULA_PASSPORT_SECTION_KEYS = LP_TEMECULA_PASSPORT_SECTIONS.map(
  (s) => s.key,
);

export function lpTemeculaPassportSectionLabel(key: string): string | undefined {
  return LP_TEMECULA_PASSPORT_SECTIONS.find((s) => s.key === key)?.label;
}

// ── Content types ────────────────────────────────────────────────────────────

export interface LpTemeculaPassportContent {
  ticker: string[];
  hero: {
    badge: string;
    collabPrefix: string;
    collabHandle: string;
    collabUrl: string;
    headline: string;
    subhead: string;
    body: string;
    bullets: string[];
    socialProof: string;
    mysteryCard: {
      overlayLabel: string;
      name: string;
      badge: string;
      imageAlt: string;
    };
  };
  couponPanel: {
    eyebrow: string;
    heading: string;
    body: string;
    fields: { label: string; placeholder: string }[];
    submit: string;
    submitPending: string;
    smsConsent: string;
    footnote: string;
    errors: { missingFields: string; shortPhone: string; generic: string };
    success: {
      headingNew: string;
      headingReturning: string;
      body: string;
      codeLabel: string;
      /** The STATIC secret code every lead sees (editable). The server also
       *  stores a per-lead tracking code that is never displayed. */
      code: string;
      copyHint: string;
      copiedPrefix: string;
    };
  };
  poster: { alt: string };
  howItWorks: {
    eyebrow: string;
    heading: string;
    intro: string;
    steps: { title: string; body: string }[];
  };
  ingredients: {
    eyebrow: string;
    heading: string;
    intro: string;
    items: { name: string; note: string }[];
    featureImageAlt: string;
    footnote: string;
  };
  gallery: {
    eyebrow: string;
    heading: string;
    intro: string;
    items: { caption: string; alt: string }[];
  };
  limitedBanner: { eyebrow: string; heading: string; body: string; cta: string };
  faq: { eyebrow: string; heading: string; items: { q: string; a: string }[] };
  bottomCta: { eyebrow: string; heading: string; body: string };
  pourBar: {
    badge: string;
    heading: string;
    intro: string;
    slides: { caption: string; alt: string }[];
    stats: { num: string; label: string }[];
    doorSign: {
      header: string;
      openBanner: string;
      hoursLabel: string;
      hours: { day: string; hours: string }[];
      addressCallout: string;
      address: string;
      mapsCta: string;
      mapsUrl: string;
    };
    contacts: {
      phoneLabel: string;
      phone: string;
      phoneHref: string;
      emailLabel: string;
      email: string;
      emailHref: string;
    };
  };
  footer: {
    tagline: string;
    collabPrefix: string;
    collabHandle: string;
    collabUrl: string;
    copyright: string;
    signoff: string;
  };
  stickyBar: { label: string; cta: string };
}

// ── Default content (VERBATIM from the export's content.json) ───────────────

export const LP_TEMECULA_PASSPORT_DEFAULT_CONTENT: LpTemeculaPassportContent = {
  ticker: [
    "THE MYSTERY DRINK IS HERE",
    "WHAT'S THE SECRET CODE?",
    "$8 SMALL · $10 BIG",
    "TEMECULA PASSPORT",
    "STAY SHAKING",
    "LIMITED QUANTITY · JULY 2–5",
  ],
  hero: {
    badge: "Independence Day Drop · Temecula, CA",
    collabPrefix: "In collaboration with",
    collabHandle: "@temeculapassport",
    collabUrl: "https://www.instagram.com/temeculapassport",
    headline: "The mystery drink is here.",
    subhead: "What's the secret code to get it?",
    body: "The Temecula Passport is a limited 4th of July cold drip mint latte: fresh muddled mint and a house-made mint syrup cold foam, smooth and cool. Drop your details to unlock your secret code, then show it at the shop ($8 small, $10 big). Poured July 2–5.",
    bullets: [
      "Unlock your secret code free",
      "$8 small · $10 big · limited quantity",
      "Poured July 2–5 in Temecula",
    ],
    socialProof: "4.9 stars · 600+ Google reviews in Temecula, CA",
    mysteryCard: {
      overlayLabel: "The mystery drink",
      name: "Temecula Passport",
      badge: "Limited · July 2–5",
      imageAlt: "Temecula Passport cold pour topped with fresh mint, viewed from above",
    },
  },
  couponPanel: {
    eyebrow: "Secret code · pay at the shop",
    heading: "What's the secret code?",
    body: "Drop your details to unlock your secret code. $8 small · $10 big at the shop, no payment now.",
    fields: [
      { label: "First name", placeholder: "Your first name" },
      { label: "Email", placeholder: "you@email.com" },
      { label: "Phone", placeholder: "(555) 123-4567" },
    ],
    submit: "Unlock my secret code",
    submitPending: "Unlocking…",
    smsConsent:
      "By submitting your phone number, you agree to receive marketing text messages from Gaffle. Message & data rates may apply. Reply STOP to unsubscribe.",
    footnote:
      "Your secret code shows up right here after you submit. Take a screenshot of it and show it at the register.",
    errors: {
      missingFields: "Please fill in all three fields.",
      shortPhone: "That phone number looks too short.",
      generic: "Something went wrong. Please try again.",
    },
    success: {
      headingNew: "You're in!",
      headingReturning: "You're already on the list!",
      body: "Here's your secret code. Screenshot it and show it at the register July 2–5 to grab the mystery drink and pay then ($8 small · $10 big).",
      codeLabel: "Your secret code",
      code: "MINTGAFFLE",
      copyHint:
        "Take a screenshot of this code and show it at the register to claim the mystery drink.",
      copiedPrefix: "Copied! ",
    },
  },
  poster: {
    alt: "Gaffle Presents: The Temecula Passport. Your ticket to summer. Cold drip latte swirled with housemade mint syrup.",
  },
  howItWorks: {
    eyebrow: "How the drop works",
    heading: "Three steps to your pour.",
    intro:
      "Unlock your secret code now so you're first in line when the mystery drink pours July 2–5.",
    steps: [
      {
        title: "Unlock your secret code",
        body: "Drop your name, email, and phone to unlock your secret code. No payment now; you pay at the shop when you pick it up.",
      },
      {
        title: "Poured July 2–5",
        body: "The mystery drink is slow-dripped and poured July 2–5 in limited quantity. Secret-code holders get first pour the moment it goes live.",
      },
      {
        title: "Show it at the register",
        body: "Screenshot your secret code, show it at the Temecula cold coffee concept July 2–5, grab the mystery drink, and pay then ($8 small, $10 big).",
      },
    ],
  },
  ingredients: {
    eyebrow: "What's inside",
    heading: "Made with the good stuff.",
    intro:
      "The Temecula Passport is a cold drip mint latte: our slow-dripped base, fresh muddled mint, and a silky cold foam made from house-made fresh mint syrup. Here's exactly what goes in the cup.",
    items: [
      {
        name: "Classic cold drip",
        note: "Our signature small-batch base, slow dripped, never heat-rushed.",
      },
      {
        name: "Fresh muddled mint",
        note: "Real mint leaves, muddled fresh to order for a bright, garden-cool lift.",
      },
      {
        name: "Your choice of milk",
        note: "Whole, oat, or none at all, pulled into a smooth, creamy mint latte.",
      },
      {
        name: "Mint syrup cold foam",
        note: "Crowned with a silky cold foam whipped from our house-made fresh mint syrup.",
      },
    ],
    featureImageAlt:
      "The Temecula Passport cup labeled with its ingredients: small-batch cold drip concentrate, house-made fresh mint cold foam, fresh organic mint leaves, and no refined sugars",
    footnote: "Every cup is poured fresh July 2–5 at the Temecula cold coffee concept.",
  },
  gallery: {
    eyebrow: "The gallery",
    heading: "Meet the mystery drink.",
    intro:
      "A closer look at the mystery drink, cold-dripped and poured fresh July 2–5 at the Temecula cold coffee concept.",
    items: [
      {
        caption: "The mystery drink",
        alt: "Fresh mint leaves resting on the creamy top of the Temecula Passport, viewed from above",
      },
      {
        caption: "Poured July 2–5",
        alt: "The Temecula Passport iced cold drip mint latte garnished with mint",
      },
      {
        caption: "Mint cold foam",
        alt: "Overhead view of the Temecula Passport's mint cold foam topped with fresh mint",
      },
      {
        caption: "The Gaffle is real",
        alt: "Illustration of the Temecula Passport cup reading The Gaffle Is Real",
      },
    ],
  },
  limitedBanner: {
    eyebrow: "Why the secret code?",
    heading: "Small-batch means small-batch.",
    body: "Every Gaffle pour is slow-dripped for hours, not rushed. The mystery drink is a limited run, poured July 2–5 only, so unlocking your secret code now puts you first in line before it sells out. No payment now; show your code at the shop and pay when you pick up.",
    cta: "Unlock the secret code",
  },
  faq: {
    eyebrow: "Common questions",
    heading: "Before you grab it.",
    items: [
      {
        q: "What's the secret code for?",
        a: "It unlocks the mystery drink, the Temecula Passport, Gaffle's limited 4th of July cold pour. Sign up to reveal your code, then grab and pay for the drink at the shop July 2–5.",
      },
      {
        q: "How much is it?",
        a: "$8 for the small and $10 for the big, paid at the shop when you pick up. Unlocking your secret code is free and doesn't charge you now.",
      },
      {
        q: "Can I bring friends?",
        a: "Please do. Have each person unlock their own secret code so everyone's on the list for the drop.",
      },
      {
        q: "When and where do I grab it?",
        a: "July 2–5 at the Gaffle pour bar in Temecula, CA. Show your secret code at the register, grab the mystery drink, and pay at the counter.",
      },
    ],
  },
  bottomCta: {
    eyebrow: "Last call",
    heading: "Unlock your secret code.",
    body: "Unlock your secret code now, then show it at the Temecula cold coffee concept July 2–5. Pay at the shop ($8 small, $10 big).",
  },
  pourBar: {
    badge: "Visit us · Temecula, CA",
    heading: "This isn't a factory. It's a cold coffee concept.",
    intro:
      "Real address. Real cold drip. Walk in, meet the crew, and try every flavor before you commit to a bottle.",
    slides: [
      {
        caption: "Pour bar · both blends on tap",
        alt: "Gaffle cold drip tap handles in red and blue, Classic and Blue Magic",
      },
      {
        caption: "The Gaffle menu wall",
        alt: "The Gaffle cafe menu painted on the wall, listing Cold Brew, Cold Drip Latte, and Signature Latte options",
      },
      {
        caption: "Inside the pour bar",
        alt: "Inside the Gaffle pour bar, center island with bottles, cold case, and pour bar in the background",
      },
    ],
    stats: [{ num: "3", label: "Cups always pouring" }],
    doorSign: {
      header: "Gaffle Pour Bar",
      openBanner: "Pouring Now",
      hoursLabel: "Hours of Operation",
      hours: [
        { day: "Monday", hours: "8 AM – 5 PM" },
        { day: "Tuesday", hours: "8 AM – 5 PM" },
        { day: "Wednesday", hours: "8 AM – 5 PM" },
        { day: "Thursday", hours: "8 AM – 5 PM" },
        { day: "Friday", hours: "8 AM – 5 PM" },
        { day: "Saturday", hours: "8 AM – 5 PM" },
        { day: "Sunday", hours: "8 AM – 5 PM" },
      ],
      addressCallout: "We have a real address. Come pour with us.",
      address: "27725 Jefferson Ave STE 103, Temecula, CA 92590",
      mapsCta: "Tap to open in Maps",
      mapsUrl: "https://maps.google.com/?q=27725+Jefferson+Ave+STE+103+Temecula+CA+92590",
    },
    contacts: {
      phoneLabel: "Call ahead",
      phone: "(404) 490-1143",
      phoneHref: "tel:+14044901143",
      emailLabel: "Wholesale",
      email: "hello@gaffle.coffee",
      emailHref: "mailto:hello@gaffle.coffee",
    },
  },
  footer: {
    tagline: "Specialty cold coffee, slow-dripped and served fresh in Temecula, California.",
    collabPrefix: "A collab with",
    collabHandle: "@temeculapassport",
    collabUrl: "https://www.instagram.com/temeculapassport",
    copyright: "© {year} Gaffle LLC. All rights reserved.",
    signoff: "Stay shaking.",
  },
  stickyBar: {
    label: "Temecula Passport · $8/$10 · July 2–5 · limited",
    cta: "Secret code",
  },
};

// ── Normalization ────────────────────────────────────────────────────────────
// Same type-preserving overlay as the cold-drip/coffee-shop templates: raw
// values are accepted only when they match the default's type at the same
// position; anything missing or mistyped falls back to the default. NOTE: any
// new editable field must be added to LpTemeculaPassportContent AND
// LP_TEMECULA_PASSPORT_DEFAULT_CONTENT or it will be dropped here.

function overlay(def: any, raw: any): any {
  if (typeof def === "string") return typeof raw === "string" ? raw : def;
  if (typeof def === "number") {
    const n = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(n) ? n : def;
  }
  if (typeof def === "boolean") return typeof raw === "boolean" ? raw : def;
  if (Array.isArray(def)) {
    if (!Array.isArray(raw)) return def;
    const template = def[0];
    if (template === undefined) return raw;
    if (typeof template === "string") {
      return raw.filter((x) => typeof x === "string" && x.trim()).slice(0, 40);
    }
    return raw
      .filter((x) => x && typeof x === "object" && !Array.isArray(x))
      .map((x) => overlay(template, x))
      .slice(0, 40);
  }
  if (def && typeof def === "object") {
    const src = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : {};
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(def)) out[k] = overlay(def[k], (src as any)[k]);
    return out;
  }
  return raw ?? def;
}

export function normalizeLpTemeculaPassportContent(
  raw: unknown,
): LpTemeculaPassportContent {
  return overlay(LP_TEMECULA_PASSPORT_DEFAULT_CONTENT, raw) as LpTemeculaPassportContent;
}

// ── Headline two-tone split ─────────────────────────────────────────────────
// The reference design renders the hero headline two-tone: the lead-in in wine
// ("THE MYSTERY DRINK") and the closing clause in red ("IS HERE."). The Sim
// copy workflow rewrites the blob against a FIXED field list and DROPS unknown
// fields (verified empirically), so a stored `headlineAccent` field would be
// lost on every copy regen. Instead the split is DERIVED at render time from
// the single stored headline: everything from the LAST standalone "is" onward
// becomes the red accent. No "is" (or the headline starts with it) → no accent.
export function splitLpTemeculaHeadline(headline: string): {
  main: string;
  accent: string;
} {
  const text = headline || "";
  const re = /\bis\b/gi;
  let lastIdx = -1;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) lastIdx = m.index;
  if (lastIdx <= 0) return { main: text, accent: "" };
  return { main: text.slice(0, lastIdx), accent: text.slice(lastIdx) };
}

// True when the blob carries enough structure to render the page.
export function isLpTemeculaPassportContentUsable(
  tp: LpTemeculaPassportContent | null | undefined,
): boolean {
  return Boolean(tp && tp.hero?.headline && tp.couponPanel?.heading);
}

// ── Regen block keys ─────────────────────────────────────────────────────────
// Per-section copy regen uses dynamic block keys "temecula-passport-<sectionKey>"
// (resolved by lpCopyBlockLabel in shared/lp-express-sections.ts), mirroring
// the coffee-shop template's "coffee-shop-<sectionKey>" precedent.

export function lpTemeculaPassportRegenKey(sectionKey: string): string {
  return `temecula-passport-${sectionKey}`;
}

export const LP_TEMECULA_PASSPORT_REGEN_KEY_RE = /^temecula-passport-([A-Za-z]+)$/;

// ── Secret code ──────────────────────────────────────────────────────────────
// The customer-facing code is STATIC and editable (couponPanel.success.code).
// This helper normalizes whatever the editor typed into a display/track-safe
// token; the server also generates a per-lead TRACKING code (never displayed)
// via lpTemeculaPassportTrackingCode.

export function lpTemeculaPassportSecretCode(raw: string | null | undefined): string {
  const clean = (raw || "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 24);
  return clean || "MINTGAFFLE";
}

const TRACKING_SUFFIX_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789"; // no 0/O/1/I/L

/** Per-lead tracking code stored on lp_express_leads.couponCode — server-side
 *  only, NEVER displayed to the lead (they always see the static secret code). */
export function lpTemeculaPassportTrackingCode(): string {
  let suffix = "";
  for (let i = 0; i < 6; i++) {
    suffix +=
      TRACKING_SUFFIX_ALPHABET[
        Math.floor(Math.random() * TRACKING_SUFFIX_ALPHABET.length)
      ];
  }
  return `TP-${suffix}`;
}

// ── AI image slots ───────────────────────────────────────────────────────────
// The export's photo binaries were not included, so photo surfaces render
// through the standard LP Express single-slot pipeline
// (SINGLE_SLOT_IMAGE_CONFIGS "temeculaPassportImages" in lp-express-worker.ts),
// stored on generatedContent.temeculaPassportImages, each with the usual
// media-picker/upload override.
//   mystery-card        → hero mystery drink card (4:3)
//   poster              → poster art section (3:4 portrait)
//   ingredients-feature → what's-inside feature photo (3:4 tall)
//   gallery-<i>         → gallery tiles (up to 4 — count follows copy)
//   pourbar-<i>         → visit-the-shop carousel slides (4:3, up to 3)
export const LP_TEMECULA_PASSPORT_IMAGE_SLOTS: {
  key: string;
  variantHint: string;
  aspectRatio?: string;
}[] = [
  {
    key: "mystery-card",
    aspectRatio: "4:3",
    variantHint:
      "Mystery product reveal photo: the limited-edition featured product shot dramatically from above on a rich dark background, styled like a teaser reveal; appetizing editorial photography, landscape composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "poster",
    aspectRatio: "3:4",
    variantHint:
      "Limited-drop promotional poster art: a bold, illustrated retro-poster-style composition celebrating the brand's limited-edition product — stylized artwork, festive summery energy, strong graphic shapes and brand colors; portrait composition filling the entire frame edge-to-edge, no borders or white margins, no legible invented text.",
  },
  {
    key: "ingredients-feature",
    aspectRatio: "3:4",
    variantHint:
      "Ingredients feature photo: the featured product shown up close with its key ingredients arranged around it, clean bright styling that makes each component readable; editorial food/product photography, portrait composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "gallery-0",
    aspectRatio: "1:1",
    variantHint:
      "Gallery photo #1: the featured limited-edition product from above, close-up hero angle showing its texture and detail; appetizing editorial photography, square composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "gallery-1",
    aspectRatio: "1:1",
    variantHint:
      "Gallery photo #2: the featured product presented in-context (in hand, on a table, or being served), visually distinct from photo #1; appetizing editorial photography, square composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "gallery-2",
    aspectRatio: "1:1",
    variantHint:
      "Gallery photo #3: a detail crop of the featured product — its topping, finish, or signature element, visually distinct from photos #1–#2; appetizing editorial photography, square composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "gallery-3",
    aspectRatio: "1:1",
    variantHint:
      "Gallery photo #4: a playful illustrated or stylized graphic of the featured product in the brand's colors (distinct from the photographic tiles); square composition filling the entire frame edge-to-edge, no borders or white margins, no legible invented text.",
  },
  {
    key: "pourbar-0",
    aspectRatio: "4:3",
    variantHint:
      "Behind-the-scenes photo #1: where the brand's product is made, packed, or presented — workspace, materials, or process in warm natural light; documentary photography, landscape composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
  {
    key: "pourbar-1",
    aspectRatio: "4:3",
    variantHint:
      "Behind-the-scenes photo #2: the brand's product lineup, ingredients, or display arranged with warm inviting atmosphere (distinct from photo #1); documentary photography, landscape composition filling the entire frame edge-to-edge, no borders or white margins, no legible invented text.",
  },
  {
    key: "pourbar-2",
    aspectRatio: "4:3",
    variantHint:
      "Behind-the-scenes photo #3: a wide view of the brand's world — the space, people, or setting where the product lives, warm and welcoming (distinct from photos #1–#2); documentary photography, landscape composition filling the entire frame edge-to-edge, no borders or white margins, no text overlays.",
  },
];

// Seed (and background-fill) only the slots the page's copy blob actually
// renders — the gallery tile count follows the copy (up to 4), and the visit
// carousel follows the slides array (up to 3). Each copy-driven slot's hint is
// grounded in the card's ACTUAL copy (same fix as the coffee-shop drink cards)
// so the prompt workflow can differentiate the images.
export function temeculaPassportImageSlotsFor(content: {
  gallery?: { items?: unknown[] };
  pourBar?: { slides?: unknown[] };
}): { key: string; variantHint: string; aspectRatio?: string }[] {
  const tiles = Array.isArray(content?.gallery?.items)
    ? (content.gallery.items as { caption?: string; alt?: string }[])
    : [];
  const slides = Array.isArray(content?.pourBar?.slides)
    ? (content.pourBar.slides as { caption?: string; alt?: string }[])
    : [];
  const tileCount = Math.min(tiles.length, 4);
  const slideCount = Math.min(slides.length, 3);
  return LP_TEMECULA_PASSPORT_IMAGE_SLOTS.filter((s) => {
    const g = /^gallery-(\d+)$/.exec(s.key);
    if (g) return Number(g[1]) < tileCount;
    const p = /^pourbar-(\d+)$/.exec(s.key);
    if (p) return Number(p[1]) < slideCount;
    return true;
  }).map((s) => {
    const g = /^gallery-(\d+)$/.exec(s.key);
    if (g) {
      const tile = tiles[Number(g[1])];
      const subject =
        (typeof tile?.alt === "string" && tile.alt.trim()) ||
        (typeof tile?.caption === "string" && tile.caption.trim()) ||
        "";
      if (!subject) return s;
      return {
        ...s,
        variantHint: `${s.variantHint} This tile is specifically about: "${subject}" — make that the focus so it reads clearly different from the page's other gallery tiles.`,
      };
    }
    const p = /^pourbar-(\d+)$/.exec(s.key);
    if (p) {
      const slide = slides[Number(p[1])];
      const subject =
        (typeof slide?.alt === "string" && slide.alt.trim()) ||
        (typeof slide?.caption === "string" && slide.caption.trim()) ||
        "";
      if (!subject) return s;
      return {
        ...s,
        variantHint: `${s.variantHint} This slide is specifically about: "${subject}" — make that the focus so it reads clearly different from the other behind-the-scenes photos.`,
      };
    }
    return s;
  });
}

// ── Brand-facts sanitizer ────────────────────────────────────────────────────
// The default blob is the literal Gaffle export. This deterministic pass runs
// server-side on EVERY generated blob and:
//   • blanks location/contact facts we have no brand data for (address, maps
//     URL, phone, email, hours callouts stay editable in the builder);
//   • blanks the Gaffle-specific collab handle/links;
//   • points every gaffle.coffee URL at the brand's own site;
//   • replaces any remaining "Gaffle" token in copy with the brand name and
//     drops the Temecula location references.
// A page generated FOR the Gaffle brand itself is left untouched.

export interface TemeculaPassportBrandFacts {
  brandName: string;
  brandUrl?: string | null;
}

function tpNormalizeUrl(raw: unknown): string {
  const s = typeof raw === "string" ? raw.trim() : "";
  if (!s) return "";
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
}

function tpMapStrings(node: unknown, fn: (s: string) => string): unknown {
  if (typeof node === "string") return fn(node);
  if (Array.isArray(node)) return node.map((x) => tpMapStrings(x, fn));
  if (node && typeof node === "object") {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(node as Record<string, unknown>)) {
      out[k] = tpMapStrings((node as Record<string, unknown>)[k], fn);
    }
    return out;
  }
  return node;
}

export function temeculaPassportBrandFactsFromRows(
  brand: any,
): TemeculaPassportBrandFacts {
  return {
    brandName: String(brand?.name || ""),
    brandUrl: brand?.url ?? null,
  };
}

export function applyTemeculaPassportBrandFacts(
  content: LpTemeculaPassportContent,
  facts: TemeculaPassportBrandFacts,
): LpTemeculaPassportContent {
  const brandName = (facts.brandName || "").trim();
  // The reference brand's own pages keep their real facts.
  if (!brandName || /gaffle/i.test(brandName)) return content;

  const siteUrl = tpNormalizeUrl(facts.brandUrl);
  const siteHost = siteUrl
    ? siteUrl.replace(/^https?:\/\/(www\.)?/i, "").replace(/\/.*$/, "")
    : "";

  // ── Structured facts first (so the generic scrub never sees them) ──
  // The IG collab is a Gaffle-specific fact — blank it (renderer hides blanks;
  // both stay editable link fields in the builder).
  content.hero.collabPrefix = "";
  content.hero.collabHandle = "";
  content.hero.collabUrl = "";
  content.footer.collabPrefix = "";
  content.footer.collabHandle = "";
  content.footer.collabUrl = "";
  if (/temecula/i.test(content.hero.badge)) content.hero.badge = "Limited Drop";
  // Location/contact facts we have no brand data for: blank, never false.
  if (/temecula/i.test(content.pourBar.badge)) content.pourBar.badge = "Visit us";
  if (/pour bar|gaffle/i.test(content.pourBar.doorSign.header)) {
    content.pourBar.doorSign.header = brandName;
  }
  if (/pour/i.test(content.pourBar.doorSign.openBanner)) {
    content.pourBar.doorSign.openBanner = "Open Now";
  }
  if (/pour/i.test(content.pourBar.doorSign.addressCallout)) {
    content.pourBar.doorSign.addressCallout = "Come say hi in person.";
  }
  content.pourBar.doorSign.address = "";
  content.pourBar.doorSign.mapsUrl = "";
  content.pourBar.doorSign.mapsCta = "";
  content.pourBar.contacts.phone = "";
  content.pourBar.contacts.phoneHref = "";
  content.pourBar.contacts.email = "";
  content.pourBar.contacts.emailHref = "";
  content.pourBar.contacts.phoneLabel = "";
  content.pourBar.contacts.emailLabel = "";
  // Gaffle's opening hours are a FACT we have no brand data for (and a model
  // "rewrite" would just fabricate hours). Keep the day rows editable but
  // blank the values — the renderer hides blank rows on the public page.
  content.pourBar.doorSign.hours = content.pourBar.doorSign.hours.map((h) => ({
    ...h,
    hours: "",
  }));
  // Cafe-residue backstop: when a stat / slide caption still reads as cafe
  // copy, blank it rather than ship Gaffle's shop on another brand's page.
  const cafeResidue =
    /\b(?:cold[\s-]?drip|cold[\s-]?brew|coffee|cafe|caf\u00e9|espresso|latte|brew(?:ing)?|pour(?:ing)?(?:\s+bar)?|on\s+tap|taps?|cups?|acidity|menu\s+wall|barista|mint)\b/i;
  content.pourBar.stats = content.pourBar.stats.map((st) =>
    cafeResidue.test(`${st.num} ${st.label}`) ? { num: "", label: "" } : st,
  );
  content.pourBar.slides = content.pourBar.slides.map((s) => ({
    ...s,
    caption: cafeResidue.test(s.caption) ? "" : s.caption,
    alt: cafeResidue.test(s.alt) ? "" : s.alt,
  }));
  if (cafeResidue.test(content.pourBar.heading)) {
    content.pourBar.heading = `Behind the scenes at ${brandName}.`;
  }
  if (cafeResidue.test(content.pourBar.intro)) {
    content.pourBar.intro = `A look at the people and the process behind ${brandName}.`;
  }
  if (/gaffle/i.test(content.footer.copyright)) {
    content.footer.copyright = `© {year} ${brandName}. All rights reserved.`;
  }
  // The static secret code defaults to a Gaffle-branded token — give other
  // brands a neutral, still-editable default.
  if (/gaffle/i.test(content.couponPanel.success.code)) {
    content.couponPanel.success.code = "SECRETDROP";
  }

  // ── Generic token scrub across every remaining string ──
  const scrub = (input: string): string => {
    let s = input;
    s = s.replace(/https?:\/\/(?:www\.)?gaffle\.coffee[^\s"'<>)]*/gi, siteUrl || "");
    s = s.replace(/[\w.+-]+@gaffle\.coffee/gi, "");
    s = s.replace(/\bgaffle\.coffee\b/gi, siteHost || brandName);
    s = s.replace(/\bgaffle('s)?\b/gi, (_m, poss) => (poss ? `${brandName}'s` : brandName));
    s = s.replace(/\btemecula\s+passport\b/gi, "the drop");
    // Gaffle's home town is a fact, not copy — drop it from prose.
    s = s.replace(/\s*(?:in\s+)?Temecula(?:\s*,\s*(?:CA(?:lifornia)?\.?))?/gi, "");
    // Tidy leftover separators from removed fragments.
    s = s.replace(/\s{2,}/g, " ").replace(/\s+([,.])/g, "$1");
    s = s.replace(/^[·,]\s*/, "").replace(/[·,]\s*$/g, "").trim();
    return s;
  };
  return tpMapStrings(content, scrub) as LpTemeculaPassportContent;
}
